import React, { useEffect, useState } from 'react';
import { Gate } from './components/Gate';
import { PortalHeader } from './components/PortalHeader';
import { Hero } from './components/Hero';
import { SectionNav } from './components/SectionNav';
import { SixBooks } from './components/SixBooks';
import { BookGrid } from './components/BookGrid';
import { ProjectSearchPanel } from './components/ProjectSearchPanel';
import { BonusStack } from './components/BonusStack';
import { AgeLibraries } from './components/AgeLibraries';
import { GettingStarted } from './components/GettingStarted';
/* ThreeSteps is retained in the codebase but no longer rendered: Getting
   started already covers the instructions once. */
import { CommunityCard } from './components/CommunityCard';
import { SupportCard } from './components/SupportCard';
/* Hidden for now: the paid unlock stack. Restore the import and render
   <UnlockStack unlocked={member.unlocked} /> inside the extras when wanted. */
// import { UnlockStack } from './components/UnlockStack'
import { mergePacks, unlockedFromUrl } from './data/tiers';
import type { PackId } from './data/tiers';
const STORE_KEY = 'workshop-member';
interface Member {
  email: string;
  unlocked: PackId[];
}
interface AppProps {
  startSignedIn?: boolean;
  startUnlocked?: PackId[];
}

/* localStorage rather than sessionStorage: this is a delivery page a parent
   comes back to for months, and making them retype their address every visit
   is exactly how people decide they have "lost" their access. */
function readStored(): Member | null {
  try {
    const saved = window.localStorage.getItem(STORE_KEY) ?? window.sessionStorage.getItem(STORE_KEY);
    if (!saved) return null;
    if (saved.startsWith('{')) {
      const parsed = JSON.parse(saved) as Partial<Member> & {
        tier?: string;
      };
      if (!parsed?.email) return null;
      return {
        email: parsed.email,
        unlocked: parsed.unlocked ?? []
      };
    }
    /* the very first version stored a bare email string */
    return {
      email: saved,
      unlocked: []
    };
  } catch {
    /* private windows block storage; the gate simply shows again */
    return null;
  }
}
function store(member: Member) {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(member));
  } catch {
    /* ignore */
  }
}
export function App({
  startSignedIn = false,
  startUnlocked = []
}: AppProps) {
  const [member, setMember] = useState<Member | null>(startSignedIn ? {
    email: 'member@example.com',
    unlocked: startUnlocked
  } : null);

  /* Whatever the delivery link carries, held separately so it still applies to
     somebody who has not typed their email yet. */
  const [fromLink, setFromLink] = useState<PackId[]>([]);
  useEffect(() => {
    if (startSignedIn) return;
    const linkPacks = typeof window === 'undefined' ? [] : unlockedFromUrl(window.location.search);
    setFromLink(linkPacks);
    const saved = readStored();
    if (!saved) return;
    const merged = {
      email: saved.email,
      unlocked: mergePacks(saved.unlocked, linkPacks)
    };
    setMember(merged);
    if (linkPacks.length) store(merged);
  }, [startSignedIn]);
  const signIn = (email: string) => {
    const next = {
      email,
      unlocked: fromLink
    };
    store(next);
    setMember(next);
  };
  const signOut = () => {
    try {
      window.localStorage.removeItem(STORE_KEY);
      window.sessionStorage.removeItem(STORE_KEY);
    } catch {
      /* ignore */
    }
    setMember(null);
  };
  if (member === null) {
    return <Gate onUnlock={signIn} data-id="element-0" data-mp="1r4siql2m1cxw" />;
  }
  return <div className="min-h-screen w-full bg-paper" data-id="element-1" data-mp="1w33oajlvh1ab">
      <PortalHeader email={member.email} onSignOut={signOut} data-id="element-2" data-mp="1w2u113s7p88b" />

      <main className="mx-auto max-w-portal px-5 pb-20 sm:px-8" data-id="element-3" data-mp="1i4cevahyg3si">
        <Hero data-id="element-4" data-mp="1m562e2rhjgkv" />
        <SectionNav data-id="element-5" data-mp="1m4wc0e2j37en" />

        <div className="flex flex-col gap-20 pt-16 sm:gap-24 sm:pt-20" data-id="element-6" data-mp="1m44q5uyqgo1k">
          <SixBooks data-id="element-7" data-mp="6onp6bdbgqo0" />
          <div className="flex flex-col gap-8" data-id="element-8" data-mp="6o5v9le4moj9">
            <BookGrid data-id="element-9" data-mp="6qiahqsi2ksm" />
            <ProjectSearchPanel data-id="element-10" data-mp="apr7r5mr10sx" />
          </div>
          <BonusStack data-id="element-11" data-mp="arkfko93lr2i" />
          <AgeLibraries data-id="element-12" data-mp="anwn7sfe3apd" />
          <GettingStarted data-id="element-13" data-mp="qwvqqbxgej5b" />
          <CommunityCard memberEmail={member.email} data-id="element-14" data-mp="qy73304ysz81" />
        </div>
      </main>

      <div className="border-t border-navy/10" data-id="element-15" data-mp="m5x5cxxlyuv7">
        <div className="mx-auto flex max-w-portal flex-col gap-2 px-5 py-8 font-sans text-[13px] text-navy/45 sm:flex-row sm:items-baseline sm:justify-between sm:px-8" data-id="element-16" data-mp="m549is06tpru">
          <p data-id="element-17" data-mp="c9am2dkxkbva">Little Engineers · Build &amp; Discover</p>
          <p data-id="element-18" data-mp="19vzup6a51mq4">Files are for your household. Please do not share the link.</p>
        </div>
      </div>

      {/* Support is deliberately the last thing on the page. Nothing after it. */}
      <SupportCard data-id="element-19" data-mp="14uu53hbryfkt" />
    </div>;
}